import { ChevronDown, ChevronRight, ExternalLink, FolderInput, Image, Images, Mic, Video, type LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AudioPlayer } from '@/features/viewers/Audio';
import { VideoPlayer } from '@/features/viewers/Video';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { useContentDetail, useObservationMediaMove } from '@/hooks/useContentQueries';
import type { MoveObservationMediaResult } from '@vivport/backend/types';
import type { MergedObservation, ObservationOption } from './observation-labels';

// Not a groupId: the destination does not exist until the move creates it.
const NEW_OBSERVATION_VALUE = 'new';

interface ProvenanceObservationCardProps {
  observation: MergedObservation;
  label: string;
  audioUrlsById?: Record<string, string>;
  onOpenContent?: (contentId: number) => void;
  /** The other Observations in this record; empty means there is nowhere to move to. */
  moveTargets?: readonly ObservationOption[];
  /** Offer "New" as a move destination — see canSplitToNewObservation. */
  allowNewObservation?: boolean;
  onMoved?: (result: MoveObservationMediaResult) => void;
}

type MediaKind = 'photo' | 'recording' | 'video';

interface ObservationMediaItem {
  id: string;
  kind: MediaKind;
  contentId: number;
}

const MEDIA_ICON: Record<MediaKind, LucideIcon> = { photo: Image, recording: Mic, video: Video };
const MEDIA_NOUN: Record<MediaKind, string> = { photo: 'Photo', recording: 'Recording', video: 'Video' };

// One Observation is one card whatever it holds, so the media kinds are DATA rather than
// branches in the markup — a further kind is one more entry in the maps above, not new
// card code. Counts are derived from the ids; nothing about the card is stored.
export function ProvenanceObservationCard({
  observation,
  label,
  audioUrlsById,
  onOpenContent,
  moveTargets,
  allowNewObservation,
  onMoved,
}: ProvenanceObservationCardProps) {
  const [expanded, setExpanded] = useState(false);

  const media: ObservationMediaItem[] = useMemo(
    () => [
      ...observation.photoIds.map((contentId) => ({
        id: `photo-${contentId}`,
        kind: 'photo' as const,
        contentId,
      })),
      ...observation.recordingIds.map((contentId) => ({
        id: `recording-${contentId}`,
        kind: 'recording' as const,
        contentId,
      })),
      ...observation.videoIds.map((contentId) => ({
        id: `video-${contentId}`,
        kind: 'video' as const,
        contentId,
      })),
    ],
    [observation.photoIds, observation.recordingIds, observation.videoIds]
  );

  const counts = [
    { key: 'photo', icon: Images, count: observation.photoIds.length },
    { key: 'recording', icon: Mic, count: observation.recordingIds.length },
    { key: 'video', icon: Video, count: observation.videoIds.length },
  ].filter((entry) => entry.count > 0);

  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="w-full rounded-lg border border-border text-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 p-3 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-medium">{label}</span>
        <span className="flex items-center gap-3 text-muted-foreground">
          {counts.map((entry) => {
            const Icon = entry.icon;
            return (
              <span key={entry.key} className="flex items-center gap-1">
                <Icon className="h-3.5 w-3.5" />
                {entry.count}
              </span>
            );
          })}
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-border">
          {media.map((item) => (
            <ObservationMediaRow
              key={item.id}
              item={item}
              audioUrl={audioUrlsById?.[item.id]}
              onOpenContent={onOpenContent}
              moveTargets={moveTargets}
              allowNewObservation={allowNewObservation}
              onMoved={onMoved}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// A row is a name until the member asks for the media itself: the list stays scannable
// when an Observation holds a dozen captures, and only what was opened costs a preview.
function ObservationMediaRow({
  item,
  audioUrl,
  onOpenContent,
  moveTargets,
  allowNewObservation,
  onMoved,
}: {
  item: ObservationMediaItem;
  audioUrl?: string;
  onOpenContent?: (contentId: number) => void;
  moveTargets?: readonly ObservationOption[];
  allowNewObservation?: boolean;
  onMoved?: (result: MoveObservationMediaResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const detail = useContentDetail(item.contentId).data;
  const move = useObservationMediaMove();

  const RowIcon = MEDIA_ICON[item.kind];
  const name =
    typeof detail?.title === 'string' && detail.title.trim().length > 0
      ? detail.title.trim()
      : `${MEDIA_NOUN[item.kind]} ${item.contentId}`;
  const description =
    typeof detail?.description === 'string' && detail.description.trim().length > 0
      ? detail.description.trim()
      : null;

  const photoUrl =
    detail?.contentType === 'PHOTO' && typeof detail.uri === 'string' && detail.uri.trim().length > 0
      ? `/api/files/photo/${detail.uri}`
      : '';
  const videoUrl =
    detail?.contentType === 'VIDEO' && typeof detail.uri === 'string' && detail.uri.trim().length > 0
      ? `/api/files/video/${detail.uri}`
      : '';
  const isRemoteRecording = detail?.contentType === 'RECORDING';
  const resolvedAudioUrl =
    audioUrl ||
    (isRemoteRecording && typeof detail.uri === 'string' && !detail.uri.startsWith('text-')
      ? `/api/files/recording/${detail.uri}`
      : undefined);
  const transcript = isRemoteRecording ? detail.transcript : null;
  const bodyText = showTranscript ? transcript : description;
  // A video is stored as-is: nothing is generated for it, so it has no placeholder to
  // wait on and an undescribed video row simply shows nothing.
  const bodyPlaceholder =
    item.kind === 'video' ? null : showTranscript ? 'Transcript processing...' : 'Summary processing...';
  const body = bodyText ?? bodyPlaceholder;

  return (
    <div className="border-b border-border last:border-b-0">
      <div className="flex items-center gap-2 px-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <RowIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs">{name}</span>
        </button>
        {(moveTargets && moveTargets.length > 0) || allowNewObservation ? (
          <Select
            value=""
            disabled={move.isPending}
            onValueChange={(next) =>
              move.mutate(
                next === NEW_OBSERVATION_VALUE
                  ? { contentId: item.contentId }
                  : { contentId: item.contentId, toObservationGroupId: Number(next) },
                { onSuccess: (result) => onMoved?.(result) }
              )
            }
          >
            <SelectTrigger
              data-testid="observation-media-move"
              aria-label={`Move ${MEDIA_NOUN[item.kind].toLowerCase()} to another observation`}
              title="Move to another observation"
              className="h-6 w-6 shrink-0 justify-center rounded-md border-0 bg-transparent p-0 shadow-none hover:bg-accent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden"
            >
              <span className="inline-flex">
                <FolderInput className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            </SelectTrigger>
            <SelectContent align="end">
              {moveTargets?.map((option) => (
                <SelectItem key={option.groupId} value={String(option.groupId)} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
              {allowNewObservation ? (
                <SelectItem value={NEW_OBSERVATION_VALUE} className="text-xs">
                  New
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
        ) : null}
        {onOpenContent ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-label={`Open ${MEDIA_NOUN[item.kind].toLowerCase()}`}
            title={`Open ${MEDIA_NOUN[item.kind].toLowerCase()}`}
            onClick={() => onOpenContent(item.contentId)}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      {open ? (
        <div className="space-y-2 px-3 pb-3">
          {item.kind === 'photo' && photoUrl ? (
            <img src={photoUrl} alt={name} className="max-h-64 w-full rounded object-contain" />
          ) : null}
          {item.kind === 'recording' && resolvedAudioUrl ? <AudioPlayer src={resolvedAudioUrl} /> : null}
          {item.kind === 'video' && videoUrl ? <VideoPlayer src={videoUrl} /> : null}

          {item.kind === 'recording' ? (
            <div className="flex items-center gap-3 text-xs">
              <button
                type="button"
                className={showTranscript ? 'text-muted-foreground' : 'font-medium'}
                onClick={() => setShowTranscript(false)}
              >
                Summary
              </button>
              <button
                type="button"
                className={showTranscript ? 'font-medium' : 'text-muted-foreground'}
                onClick={() => setShowTranscript(true)}
              >
                Transcript
              </button>
            </div>
          ) : null}

          {body !== null ? (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">{body}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
